import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import logo from '../../assets/img/56.png';

const LoginPage = ({ setCurrentPage, setUserRole, setAuthData }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Estado para 2FA
  const [show2FA, setShow2FA] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [message2FA, setMessage2FA] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

  // Función para procesar login exitoso
  const handleSuccessfulLogin = (data) => {
    const primaryRole = (data.roles && data.roles[0]?.nombre) || 'estudiante';
    setUserRole(primaryRole);
    setAuthData(data);

    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('menus', JSON.stringify(data.menus));
    
    // Guardar el token JWT real devuelto por el servidor
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    
    if (data.etapa_academica) {
      localStorage.setItem('etapa_academica', data.etapa_academica);
    }

    let targetPage;
    if (primaryRole === 'estudiante') {
      switch (data.etapa_academica) {
        case 'preadmision_curso':
          targetPage = 'dashboard-preadmision-curso';
          break;
        case 'preadmision_prueba':
          targetPage = 'dashboard-preadmision-prueba';
          break;
        case 'admision_aprobado':
        default:
          targetPage = 'dashboard-estudiante';
          break;
      }
    } else {
      const roleToPage = {
        administrador: 'dashboard-administrador',
        kardex: 'dashboard-kardex',
        docente: 'dashboard-docente',
        'jefatura de carrera': 'dashboard-jefatura',
        'dirección académica': 'dashboard-direccion',
        'cursos_extension': 'dashboard-cursos-extension',
        'rectorado': 'dashboard-rectorado',
      };
      targetPage = roleToPage[primaryRole] || 'dashboard-estudiante';
    }

    setCurrentPage(targetPage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { username: '', password: '' };
    if (!username.trim()) newErrors.username = 'El usuario es obligatorio';
    if (!password.trim()) newErrors.password = 'La contraseña es obligatoria';
    setErrors(newErrors);
    if (newErrors.username || newErrors.password) {
      return;
    }

    try {
      setLoading(true);
      const resp = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: username, password }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || 'Credenciales inválidas');
      }

      const data = await resp.json();

      // Si requiere 2FA
      if (data.requires2FA) {
        setPendingUserId(data.userId);
        setMaskedEmail(data.emailMasked || '');
        setShow2FA(true);
        setMessage2FA('Código enviado a tu correo');
        return;
      }

      // Login directo (2FA deshabilitado)
      handleSuccessfulLogin(data);

    } catch (error) {
      setErrors((prev) => ({ ...prev, password: error.message }));
    } finally {
      setLoading(false);
    }
  };

  // Verificar código 2FA
  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length < 4) {
      setMessage2FA('Ingresa el código de 4 dígitos');
      return;
    }

    try {
      setLoading(true);
      setMessage2FA('');

      const resp = await fetch(`${API_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pendingUserId, code: verificationCode }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        setMessage2FA(data.message || 'Código incorrecto');
        return;
      }

      // Verificación exitosa
      handleSuccessfulLogin(data);

    } catch (error) {
      setMessage2FA('Error al verificar código');
    } finally {
      setLoading(false);
    }
  };

  // Volver al login desde 2FA
  const handleBack2FA = () => {
    setShow2FA(false);
    setPendingUserId(null);
    setVerificationCode('');
    setMessage2FA('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header azul oscuro */}
      <div className="bg-[#1e3a8a] h-3"></div>

      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-md mx-auto">
          {/* Botón Volver */}
          <button
            onClick={() => setCurrentPage('landing')}
            className="flex items-center gap-2 text-[#1e3a8a] hover:underline font-medium mb-8"
          >
            <ArrowLeft size={20} />
            Volver
          </button>

          {/* Logo y Título centrados */}
          <div className="text-center mb-10">
            <div className="flex justify-center items-center gap-4 mb-2">
              <img
                src={logo}
                alt="EBID Logo"
                className="w-24 h-24 md:w-28 md:h-28 object-contain"
              />
              <div className="text-left">
                <h1 className="text-xl md:text-2xl font-bold text-[#2c3e7d]">Sistema Académico</h1>
                <p className="text-[#2c3e7d] text-xs md:text-sm text-right">Escuela Boliviana</p>
                <p className="text-[#2c3e7d] text-xs md:text-sm text-right">Intercultural de Danza</p>
              </div>
            </div>
          </div>

          {/* Contenedor del formulario */}
          <div className="bg-white w-full">
            {show2FA ? (
              /* Formulario de verificación 2FA */
              <div className="space-y-5">
                <button
                  onClick={handleBack2FA}
                  className="flex items-center gap-2 text-[#2c3e7d] hover:underline text-sm"
                >
                  <ArrowLeft size={16} />
                  Volver
                </button>

                <div className="text-center">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Verificación de Seguridad</h2>
                  <p className="text-sm text-gray-600">
                    Ingresa el código de 4 dígitos enviado a
                  </p>
                  <p className="text-sm font-medium text-[#1e3a8a]">{maskedEmail}</p>
                </div>

                <div>
                  <input
                    type="text"
                    maxLength={4}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleVerify2FA();
                      }
                    }}
                    className="w-full px-5 py-4 border-2 rounded-xl text-center text-3xl tracking-[0.5em] font-mono border-gray-300 focus:border-blue-500 focus:outline-none transition"
                    placeholder="0000"
                    autoFocus
                  />
                </div>

                {message2FA && (
                  <p className={`text-center text-sm ${message2FA.includes('enviado') ? 'text-green-600' : 'text-red-600'}`}>
                    {message2FA}
                  </p>
                )}

                <button
                  onClick={handleVerify2FA}
                  className={`w-full bg-[#1e3a8a] text-white py-3.5 rounded-full hover:bg-[#2c4a9a] transition font-semibold text-base shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Verificando...' : 'Verificar Código'}
                </button>

                <p className="text-center text-xs text-gray-500">
                  El código expira en 5 minutos
                </p>
              </div>
            ) : (
              /* Formulario de Login normal */
              <div className="space-y-5">
                <div>
                  <label className="block text-gray-700 text-sm mb-2 font-medium">Usuario</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      const v = e.target.value;
                      setUsername(v);
                      if (v.trim()) {
                        setErrors((prev) => ({ ...prev, username: '' }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit(e);
                      }
                    }}
                    className={`w-full px-5 py-3 border-2 rounded-full focus:outline-none transition ${errors.username ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm mb-2 font-medium">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPassword(v);
                        if (v.trim()) {
                          setErrors((prev) => ({ ...prev, password: '' }));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit(e);
                        }
                      }}
                      className={`w-full px-5 py-3 pr-12 border-2 rounded-full focus:outline-none transition ${errors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setCurrentPage('recuperar')}
                    className="text-[#2c3e7d] hover:underline font-medium text-sm"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                <button
                  onClick={handleSubmit}
                  className={`w-full bg-[#1e3a8a] text-white py-3.5 rounded-full hover:bg-[#2c4a9a] transition font-semibold text-base shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Ingresando...' : 'Ingresar'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;